import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, AlertCircle, CheckCircle, Car, Paperclip, FileText, Stethoscope } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { isValidEmail, readFunctionError } from '../../../../lib/email';
import { EMAIL_TEMPLATES, templateById, type EmailTemplateContext } from './emailTemplates';
import { buildServiceHistoryPdfBase64 } from '../buildServiceHistoryPdf';
import { buildDiagnosticPdfBase64 } from '../../Workshop/Diagnostics/buildDiagnosticPdf';
import { errorMessage } from '../../../../lib/errors';

export interface EmailVehicle {
    id?: string;
    vrm: string;
    make?: string | null;
    model?: string | null;
    year?: string | number | null;
    mot_expiry?: string | null;
}

interface CustomerEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerEmail: string;
    customerName: string;
    orgId: string;
    businessEmail?: string;
    onSuccess?: () => void;
    /** Optional pre-filled composer (e.g. an MOT reminder). */
    initialSubject?: string;
    initialMessage?: string;
    /** Template to open on, e.g. 'mot_reminder'. Defaults to the picker. */
    initialTemplateId?: string;
    /** Links the send to the customer record so it shows in their history. */
    customerId?: string;
    /** Trading name used to sign off the templates. */
    businessName?: string;
    /** The customer's vehicles — templates use the selected one. */
    vehicles?: EmailVehicle[];
    /** Pre-select this vehicle (id) when opening. */
    initialVehicleId?: string;
    /** Whether this customer has any service history — gates that attachment. */
    hasServiceHistory?: boolean;
    /** The customer's diagnostic reports, newest first, for the attachment picker. */
    diagnosticReports?: EmailDiagnosticReport[];
}

/** Just enough of a diagnostic_reports row to label it in the picker. */
export interface EmailDiagnosticReport {
    id: string;
    created_at: string;
    vehicle_id?: string | null;
    vehicle_vrm?: string | null;
    findingCount?: number;
}

/**
 * What the composer can attach. Both are generated at send time from live data
 * rather than pulled from storage, so a document is never stale relative to the
 * record it describes.
 */
type AttachmentKind = 'service_history' | 'diagnostic';

const vehicleLabel = (v?: EmailVehicle): string => {
    if (!v) return '';
    const desc = [v.year, v.make, v.model].filter(Boolean).join(' ');
    return desc ? `your ${desc} (${v.vrm})` : `your vehicle (${v.vrm})`;
};

/**
 * Customer email composer. Opens on a list of ready-made templates — MOT
 * reminder, service due, payment chase, ready for collection — so a message is
 * one tap plus a tweak, never a blank box. Posts to the authenticated
 * `send-customer-email` edge function, which wraps the message in the org's
 * branding, sends it, and records it against the customer.
 */
export default function CustomerEmailModal({
    isOpen,
    onClose,
    customerEmail,
    customerName,
    orgId,
    businessEmail,
    onSuccess,
    initialSubject,
    initialMessage,
    initialTemplateId,
    customerId,
    businessName,
    vehicles = [],
    initialVehicleId,
    hasServiceHistory,
    diagnosticReports = [],
}: CustomerEmailModalProps) {
    const [emailTo, setEmailTo] = useState(customerEmail);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sendCopy, setSendCopy] = useState(false);
    const [status, setStatus] = useState<'idle' | 'generating' | 'sending' | 'success'>('idle');
    const [error, setError] = useState<string | null>(null);

    // 'pick' shows the template gallery, 'compose' the editable email.
    const [step, setStep] = useState<'pick' | 'compose'>('pick');
    const [templateId, setTemplateId] = useState('custom');
    const [vehicleId, setVehicleId] = useState<string | undefined>(initialVehicleId);

    // Attachments are chosen, not implied by the template. Picking "Health
    // check results" ticks the report for you, but every template can carry
    // any of these — the mechanic decides what the customer needs to see.
    const [attachKinds, setAttachKinds] = useState<Set<AttachmentKind>>(new Set());
    const [diagnosticId, setDiagnosticId] = useState<string | undefined>(undefined);

    const selectedVehicle = vehicles.find((v) => v.id === vehicleId) ?? vehicles[0];
    const template = templateById(templateId);

    // Only offer a diagnostic for the vehicle in the copy. A report for the
    // other car on the account is nearly always the wrong document, and its
    // reg on the front page is what the customer notices.
    const availableDiagnostics = selectedVehicle?.id
        ? diagnosticReports.filter((d) => d.vehicle_id === selectedVehicle.id)
        : diagnosticReports;
    const canAttachDiagnostic = availableDiagnostics.length > 0;
    const canAttachServiceHistory = hasServiceHistory !== false;
    const selectedDiagnostic =
        availableDiagnostics.find((d) => d.id === diagnosticId) ?? availableDiagnostics[0];

    const toggleAttachment = (kind: AttachmentKind) => {
        setAttachKinds((prev) => {
            const next = new Set(prev);
            if (next.has(kind)) next.delete(kind);
            else next.add(kind);
            return next;
        });
    };

    const buildContext = (v?: EmailVehicle): EmailTemplateContext => ({
        firstName: (customerName || '').trim().split(/\s+/)[0] || 'there',
        businessName: businessName || 'Your workshop',
        vehicleLabel: vehicleLabel(v),
        vrm: v?.vrm || '',
        motDue: v?.mot_expiry
            ? new Date(v.mot_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            : '',
    });

    useEffect(() => {
        if (!isOpen) return;
        setEmailTo(customerEmail);
        setStatus('idle');
        setError(null);
        setSendCopy(false);
        setVehicleId(initialVehicleId ?? vehicles[0]?.id);
        setAttachKinds(new Set());
        setDiagnosticId(undefined);

        // A caller that supplies its own copy (or names a template) skips the
        // picker and goes straight to the composer.
        if (initialSubject || initialMessage) {
            setTemplateId(initialTemplateId ?? 'custom');
            setSubject(initialSubject ?? '');
            setMessage(initialMessage ?? '');
            setStep('compose');
            return;
        }
        if (initialTemplateId) {
            const t = templateById(initialTemplateId);
            const ctx = buildContext(vehicles.find((v) => v.id === (initialVehicleId ?? vehicles[0]?.id)));
            setTemplateId(t.id);
            setSubject(t.subject(ctx));
            setMessage(t.body(ctx));
            setStep('compose');
            return;
        }
        setTemplateId('custom');
        setSubject('');
        setMessage('');
        setStep('pick');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, customerEmail, customerName, initialSubject, initialMessage, initialTemplateId, initialVehicleId]);

    const applyTemplate = (id: string, v?: EmailVehicle) => {
        const t = templateById(id);
        const ctx = buildContext(v ?? selectedVehicle);
        setTemplateId(id);
        setSubject(t.subject(ctx));
        setMessage(t.body(ctx));

        // A default, not a rule: the template suggests what usually belongs
        // with it, and only when we can actually produce that document. The
        // user unticks it or adds the other one in the composer.
        const wanted = t.defaultAttachment;
        const buildable =
            wanted === 'service_history' ? canAttachServiceHistory
            : wanted === 'diagnostic' ? canAttachDiagnostic
            : false;
        setAttachKinds(wanted && buildable ? new Set<AttachmentKind>([wanted]) : new Set<AttachmentKind>());

        setStep('compose');
    };

    const changeVehicle = (id: string) => {
        setVehicleId(id);
        // Re-render the current template against the newly picked vehicle so the
        // reg / MOT date in the copy stays true.
        const v = vehicles.find((x) => x.id === id);
        const t = templateById(templateId);
        if (templateId !== 'custom' && t.usesVehicle) {
            const ctx = buildContext(v);
            setSubject(t.subject(ctx));
            setMessage(t.body(ctx));
        }
    };

    const handleClose = () => {
        if (status === 'sending') return;
        setError(null);
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!emailTo.trim()) {
            setError('Recipient email is required');
            return;
        }
        if (!isValidEmail(emailTo)) {
            setError('That email address looks invalid — check it includes a domain like "name@example.com".');
            return;
        }
        if (!subject.trim()) {
            setError('Please add a subject.');
            return;
        }
        if (!message.trim()) {
            setError('Please write a message.');
            return;
        }

        try {
            // Every attachment is rendered here, from live data, at the moment
            // of sending — so what lands in the customer's inbox matches the
            // record as it stands now rather than whenever a file was last
            // written to storage.
            const attachments: Array<{ filename: string; content: string }> = [];

            if (attachKinds.size > 0) {
                setStatus('generating');
            }

            if (attachKinds.has('service_history')) {
                if (!customerId) {
                    setError('Could not attach the service history — customer record is missing.');
                    setStatus('idle');
                    return;
                }
                const built = await buildServiceHistoryPdfBase64({
                    orgId,
                    customerId,
                    vehicleId: selectedVehicle?.id,
                    customerName,
                    customerEmail: emailTo,
                    showPrices: false,
                });
                if (!built || built.recordCount === 0) {
                    setError('There are no service records to attach for this vehicle.');
                    setStatus('idle');
                    return;
                }
                attachments.push({ filename: built.fileName, content: built.pdfBase64 });
            }

            if (attachKinds.has('diagnostic')) {
                if (!selectedDiagnostic) {
                    setError('Pick which health check to attach.');
                    setStatus('idle');
                    return;
                }
                const built = await buildDiagnosticPdfBase64({
                    orgId,
                    reportId: selectedDiagnostic.id,
                    customerName,
                    customerEmail: emailTo,
                });
                if (!built) {
                    // Better to stop than to send a "please find attached"
                    // with nothing attached — the customer has no way to know
                    // the document was meant to be there.
                    setError('Could not build the health check PDF. Try again, or send without it.');
                    setStatus('idle');
                    return;
                }
                attachments.push({ filename: built.fileName, content: built.pdfBase64 });
            }

            setStatus('sending');
            const { data, error: fnError } = await supabase.functions.invoke('send-customer-email', {
                body: {
                    org_id: orgId,
                    email: emailTo.trim(),
                    customer_name: customerName,
                    customer_id: customerId,
                    template_type: templateId,
                    subject: subject.trim(),
                    message: message.trim(),
                    send_copy: sendCopy,
                    business_email: businessEmail,
                    ...(attachments.length > 0 ? { attachments } : {}),
                },
            });

            if (fnError) throw await readFunctionError(fnError, 'Failed to send email');
            if (data?.error) throw new Error(data.error);

            setStatus('success');
            setTimeout(() => {
                onSuccess?.();
                onClose();
            }, 1500);
        } catch (err) {
            console.error('Customer email error:', err);
            setError(errorMessage(err) || 'Failed to send email');
            setStatus('idle');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-lg max-h-[90vh] flex flex-col bg-panel border border-line rounded-xl shadow-2xl overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-tint/10 shrink-0">
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold text-fg flex items-center gap-2">
                            <Send className="w-5 h-5 text-accent" />
                            Email Customer
                        </h2>
                        <p className="text-xs text-fg-4 mt-0.5 truncate">
                            {step === 'pick' ? 'Pick a template to start from' : template.label}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-tint/10 transition-colors text-fg-3 hover:text-fg"
                        disabled={status === 'generating' || status === 'sending'}
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {step === 'pick' ? (
                    /* ── Template gallery ─────────────────────────────────── */
                    <div className="p-6 space-y-2 overflow-y-auto">
                        {vehicles.length > 1 && (
                            <label className="block pb-2">
                                <span className="text-xs font-bold text-fg-4 uppercase">Vehicle used in the copy</span>
                                <select
                                    value={vehicleId ?? ''}
                                    onChange={(e) => setVehicleId(e.target.value)}
                                    className="mt-1 w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2.5 text-fg outline-none focus:ring-2 focus:ring-accent"
                                >
                                    {vehicles.map((v) => (
                                        <option key={v.id ?? v.vrm} value={v.id ?? ''} className="bg-card">
                                            {[v.vrm, v.make, v.model].filter(Boolean).join(' · ')}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}

                        {EMAIL_TEMPLATES.map((t) => {
                            // Only "Service history" is genuinely unusable
                            // without its document — its whole body is "please
                            // find attached". Everything else reads fine on its
                            // own, so attachments never block a template.
                            const disabled = t.defaultAttachment === 'service_history' && !canAttachServiceHistory;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => !disabled && applyTemplate(t.id)}
                                    disabled={disabled}
                                    className="w-full text-left p-3.5 rounded-lg bg-tint/5 border border-tint/10 hover:border-accent/60 hover:bg-tint/10 transition-colors disabled:opacity-40 disabled:hover:border-tint/10 disabled:hover:bg-tint/5 disabled:cursor-not-allowed"
                                >
                                    <p className="text-sm font-bold text-fg">{t.label}</p>
                                    <p className="text-xs text-fg-4 mt-0.5">
                                        {disabled ? 'No service history recorded yet' : t.hint}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    /* ── Composer ─────────────────────────────────────────── */
                    <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
                        <div className="flex items-center justify-between gap-3">
                            <button
                                type="button"
                                onClick={() => setStep('pick')}
                                disabled={status !== 'idle'}
                                className="text-xs font-bold text-accent hover:underline disabled:opacity-50"
                            >
                                ← Change template
                            </button>
                            {vehicles.length > 1 && template.usesVehicle && (
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <Car className="w-3.5 h-3.5 text-fg-4 shrink-0" />
                                    <select
                                        value={vehicleId ?? ''}
                                        onChange={(e) => changeVehicle(e.target.value)}
                                        disabled={status !== 'idle'}
                                        className="bg-tint/5 border border-tint/10 rounded px-2 py-1 text-xs text-fg outline-none max-w-[180px]"
                                    >
                                        {vehicles.map((v) => (
                                            <option key={v.id ?? v.vrm} value={v.id ?? ''} className="bg-card">
                                                {[v.vrm, v.make, v.model].filter(Boolean).join(' · ')}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* To */}
                        <div>
                            <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                To <span className="text-danger">*</span>
                            </label>
                            <input
                                type="email"
                                required
                                value={emailTo}
                                onChange={(e) => setEmailTo(e.target.value)}
                                disabled={status !== 'idle'}
                                className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none disabled:opacity-50"
                            />
                        </div>

                        {/* Subject */}
                        <div>
                            <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                Subject <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                required
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                disabled={status !== 'idle'}
                                placeholder="e.g. Your MOT is due soon"
                                className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none disabled:opacity-50 placeholder:text-fg-4"
                            />
                        </div>

                        {/* Message */}
                        <div>
                            <label className="block text-xs font-bold text-fg-4 uppercase mb-1">Message</label>
                            <textarea
                                rows={8}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={status !== 'idle'}
                                className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none resize-none disabled:opacity-50"
                            />
                        </div>

                        {/* Attachments.
                            Previously implicit: a template either carried its
                            PDF or it did not, and there was no way to send the
                            health check with a "ready for collection", or the
                            service history with anything at all. */}
                        <div className="rounded-lg border border-tint/10 bg-tint/5 p-3 space-y-2">
                            <p className="flex items-center gap-1.5 text-xs font-bold text-fg-4 uppercase">
                                <Paperclip className="w-3.5 h-3.5" /> Attach
                            </p>

                            <AttachmentToggle
                                icon={<FileText className="w-4 h-4" />}
                                label="Service history"
                                hint={
                                    canAttachServiceHistory
                                        ? 'Everything we have done on this vehicle, as a PDF.'
                                        : 'Nothing on record for this customer yet.'
                                }
                                checked={attachKinds.has('service_history')}
                                disabled={!canAttachServiceHistory || status !== 'idle'}
                                onChange={() => toggleAttachment('service_history')}
                            />

                            <AttachmentToggle
                                icon={<Stethoscope className="w-4 h-4" />}
                                label="Health check report"
                                hint={
                                    canAttachDiagnostic
                                        ? 'Findings, how urgent each one is, and an estimate to put them right.'
                                        : 'No health check recorded for this vehicle yet.'
                                }
                                checked={attachKinds.has('diagnostic')}
                                disabled={!canAttachDiagnostic || status !== 'idle'}
                                onChange={() => toggleAttachment('diagnostic')}
                            />

                            {/* Which report, when there is more than one. */}
                            {attachKinds.has('diagnostic') && availableDiagnostics.length > 1 && (
                                <label className="block pl-1">
                                    <span className="text-fd-3xs font-bold text-fg-4 uppercase tracking-wider">
                                        Which health check
                                    </span>
                                    <select
                                        value={selectedDiagnostic?.id ?? ''}
                                        onChange={(e) => setDiagnosticId(e.target.value)}
                                        disabled={status !== 'idle'}
                                        className="mt-1 w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-sm text-fg outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                                    >
                                        {availableDiagnostics.map((d) => (
                                            <option key={d.id} value={d.id} className="bg-card">
                                                {new Date(d.created_at).toLocaleDateString('en-GB', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric',
                                                })}
                                                {d.vehicle_vrm ? ` · ${d.vehicle_vrm}` : ''}
                                                {typeof d.findingCount === 'number'
                                                    ? ` · ${d.findingCount} finding${d.findingCount === 1 ? '' : 's'}`
                                                    : ''}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                        </div>

                        {/* Send copy */}
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="sendCustomerCopy"
                                checked={sendCopy}
                                onChange={(e) => setSendCopy(e.target.checked)}
                                disabled={status !== 'idle'}
                                className="rounded border-tint/10 bg-tint/5 text-accent focus:ring-accent disabled:opacity-50"
                            />
                            <label htmlFor="sendCustomerCopy" className="text-sm text-fg-3 select-none cursor-pointer">
                                Send a copy to my email {businessEmail && `(${businessEmail})`}
                            </label>
                        </div>

                        {/* Error */}
                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm"
                                >
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    {error}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Actions */}
                        <div className="pt-4 flex gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 px-4 py-2.5 rounded-lg font-bold text-fg-2 hover:bg-tint/5 border border-tint/10 transition-colors disabled:opacity-50"
                                disabled={status === 'generating' || status === 'sending'}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={status !== 'idle'}
                                className={`flex-1 font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 min-w-[120px] ${status === 'success' ? 'bg-success-fill text-on-fill' : 'fd-btn-primary'}`}
                            >
                                {status === 'idle' && (<><Send className="w-5 h-5" />Send Email</>)}
                                {status === 'generating' && (<><Loader2 className="w-5 h-5 animate-spin" />Preparing attachments…</>)}
                                {status === 'sending' && (<><Loader2 className="w-5 h-5 animate-spin" />Sending...</>)}
                                {status === 'success' && (<><CheckCircle className="w-5 h-5" />Sent!</>)}
                            </button>
                        </div>
                    </form>
                )}
            </motion.div>
        </div>
    );
}

/**
 * A full-row checkbox rather than a bare tickbox: on a phone the label and the
 * hint are the tap target, which is the only way this clears 44px without
 * making the row twice as tall as it needs to be.
 */
const AttachmentToggle = ({
    icon,
    label,
    hint,
    checked,
    disabled,
    onChange,
}: {
    icon: React.ReactNode;
    label: string;
    hint: string;
    checked: boolean;
    disabled: boolean;
    onChange: () => void;
}) => (
    <label
        className={`flex items-start gap-2.5 rounded-lg px-2 py-2 min-h-[44px] transition-colors ${
            disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-tint/5'
        }`}
    >
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            className="mt-0.5 shrink-0 rounded border-tint/10 bg-tint/5 text-accent focus:ring-accent"
        />
        <span className="text-fg-4 shrink-0 mt-0.5">{icon}</span>
        <span className="min-w-0">
            <span className="block text-sm font-medium text-fg">{label}</span>
            <span className="block text-xs text-fg-4">{hint}</span>
        </span>
    </label>
);
